import { RefObject, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CheckVerifiedDomainStatus } from "@evennt-tech/api-v2-external";
import { Roles } from "@evennt-tech/api-v2-core";
import { selectUser } from "store/auth/selectors";
import { extractOrigin } from "tools/url/extractDomain";
import { useCheckVerifiedDomains } from "hooks/verifiedDomain/useCheckVerifiedDomains";

const IFRAME_WRAP_CLASS = "ffair-iframe-wrap";
const QUILL_VIDEO_CLASS = "ql-video";
// Player URLs Quill's video button produces (`extractVideoUrl`):
// youtube.com/watch?v=… → www.youtube.com/embed/<id>,
// vimeo.com/<id> → player.vimeo.com/video/<id>/.
const VIDEO_PLAYER_EMBEDS: { host: string; path: RegExp }[] = [
  { host: "www.youtube.com", path: /^\/embed\/[A-Za-z0-9_-]+$/ },
  { host: "player.vimeo.com", path: /^\/video\/\d+\/?$/ },
];

// The editor's video button (Quill `video` blot) saves a bare
// `<iframe class="ql-video">`, while the IFrame button always wraps its iframe
// in `.ffair-iframe-wrap`. Only the former, pointing at a YouTube or Vimeo
// player, skips domain verification; anything else stays gated.
const isVideoButtonEmbed = (iframe: HTMLIFrameElement): boolean => {
  if (!iframe.classList.contains(QUILL_VIDEO_CLASS)) return false;
  if (iframe.closest(`.${IFRAME_WRAP_CLASS}`)) return false;
  try {
    const url = new URL((iframe.getAttribute("src") || "").trim());
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      VIDEO_PLAYER_EMBEDS.some(
        ({ host, path }) => url.hostname === host && path.test(url.pathname)
      )
    );
  } catch {
    return false;
  }
};

const queryGatedIframes = (root: ParentNode): HTMLIFrameElement[] =>
  Array.from(root.querySelectorAll<HTMLIFrameElement>("iframe")).filter(
    (node) => !isVideoButtonEmbed(node)
  );

const removeWrap = (iframe: HTMLIFrameElement) => {
  const wrap = iframe.closest(`.${IFRAME_WRAP_CLASS}`);
  if (wrap) {
    wrap.remove();
  } else {
    iframe.remove();
  }
};

const normalizeIframeSrcCandidate = (
  value: string | null | undefined
): string => {
  const src = (value || "").trim();
  return src && src !== "about:blank" ? src : "";
};

const getIframeSrc = (iframe: HTMLIFrameElement): string => {
  const wrap = iframe.closest<HTMLElement>(`.${IFRAME_WRAP_CLASS}`);
  const dataSrc = normalizeIframeSrcCandidate(wrap?.getAttribute("data-src"));
  if (dataSrc) return dataSrc;

  const attrSrc = normalizeIframeSrcCandidate(iframe.getAttribute("src"));
  if (attrSrc) return attrSrc;

  return normalizeIframeSrcCandidate(iframe.src);
};

const collectIframeMatchCandidates = (root: ParentNode): string[] => {
  const out = new Set<string>();
  queryGatedIframes(root).forEach((node) => {
    const src = getIframeSrc(node);
    if (src) out.add(src);
    const origin = extractOrigin(src);
    if (origin) out.add(origin);
  });
  return Array.from(out);
};

const isIframeApproved = (
  iframe: HTMLIFrameElement,
  checkResult: Map<string, CheckVerifiedDomainStatus>
): boolean => {
  const src = getIframeSrc(iframe);
  if (
    src &&
    checkResult.get(src.toLowerCase()) === CheckVerifiedDomainStatus.APPROVED
  )
    return true;
  const origin = extractOrigin(src);
  if (
    origin &&
    checkResult.get(origin.toLowerCase()) === CheckVerifiedDomainStatus.APPROVED
  )
    return true;
  return false;
};

const restoreApprovedIframe = (iframe: HTMLIFrameElement) => {
  const src = getIframeSrc(iframe);
  const currentSrc = normalizeIframeSrcCandidate(iframe.getAttribute("src"));

  if (src && currentSrc !== src) {
    iframe.setAttribute("src", src);
  }
  if (!iframe.style.display || iframe.style.display === "none") {
    iframe.style.display = "block";
  }
  if (iframe.style.visibility === "hidden") {
    iframe.style.removeProperty("visibility");
  }
};

export const useFilterUnverifiedIframes = (
  containerRef: RefObject<HTMLDivElement>,
  content: string | undefined
): void => {
  const { role } = useSelector(selectUser);
  const isExhibitor = role === Roles.EXHIBITOR;

  const [domains, setDomains] = useState<string[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current
      .querySelectorAll<HTMLIFrameElement>("iframe")
      .forEach((node) => {
        if (node.style.pointerEvents === "none") {
          node.style.removeProperty("pointer-events");
        }
      });
    containerRef.current
      .querySelectorAll(".ffair-iframe-toolbar")
      .forEach((node) => node.remove());
  }, [containerRef, content]);

  useEffect(() => {
    if (!isExhibitor || !containerRef.current) {
      return;
    }
    setDomains(collectIframeMatchCandidates(containerRef.current));
  }, [containerRef, content, isExhibitor]);

  const { result: checkResult, isReady } = useCheckVerifiedDomains(domains, {
    enabled: isExhibitor,
  });

  useEffect(() => {
    if (!isExhibitor || !containerRef.current) return;
    if (!isReady) return;

    const iframeNodes = queryGatedIframes(containerRef.current);

    if (iframeNodes.length > 0 && domains.length === 0) return;

    iframeNodes.forEach((node) => {
      if (isIframeApproved(node, checkResult)) {
        restoreApprovedIframe(node);
      } else {
        removeWrap(node);
      }
    });
  }, [containerRef, content, checkResult, isReady, isExhibitor, domains]);
};
